import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { CalendarDays, CheckCircle2, Clock3, Copy, Mail, MessageCircle, Plus, Repeat2, Search } from 'lucide-react';

const DEFAULT_FORM = {
  title: '',
  amount: '',
  currency: 'UGX',
  billing_cycle: 'monthly',
  start_date: '',
  next_due_date: '',
  end_date: '',
  status: 'active',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  customer_location: '',
};

function fmtMoney(currency, value) {
  const n = Number(value || 0);
  return `${currency || 'UGX'} ${Number.isNaN(n) ? value : n.toLocaleString()}`;
}

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function statusClass(status, overdue) {
  if (overdue) return 'bg-red-100 text-red-800';
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'bg-emerald-100 text-emerald-800';
  if (s === 'paused') return 'bg-amber-100 text-amber-800';
  if (s === 'completed') return 'bg-slate-100 text-slate-700';
  if (s === 'cancelled') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
}

export default function SubscriptionsPage() {
  const [data, setData] = useState({ subscriptions: [], upcoming: [], overdue: [], access: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState(DEFAULT_FORM);

  const canUseSubscriptions = !!data?.access?.can_use_subscriptions;

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/subscriptions');
      setData(res.data || { subscriptions: [], upcoming: [], overdue: [], access: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscriptions');
      setData({ subscriptions: [], upcoming: [], overdue: [], access: {} });
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    if (!id) {
      setSelected(null);
      return;
    }
    try {
      const res = await api.get(`/dashboard/subscriptions/${id}`);
      setSelected(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscription');
      setSelected(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setSelectedId('');
    setForm(DEFAULT_FORM);
  };

  const beginEdit = (item) => {
    setSelectedId(item.id);
    setForm({
      title: item.title || '',
      amount: item.amount || '',
      currency: item.currency || 'UGX',
      billing_cycle: item.billing_cycle || 'monthly',
      start_date: item.start_date || '',
      next_due_date: item.next_due_date || '',
      end_date: item.end_date || '',
      status: item.status || 'active',
      customer_name: item.customer?.name || '',
      customer_phone: item.customer?.phone || '',
      customer_email: item.customer?.email || '',
      customer_location: item.customer?.location || '',
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canUseSubscriptions) {
      toast.error('Upgrade your package to use subscriptions.');
      return;
    }
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Amount must be greater than 0');
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        amount: Number(form.amount),
        currency: form.currency,
        billing_cycle: form.billing_cycle,
        start_date: form.start_date,
        next_due_date: form.next_due_date,
        end_date: form.end_date,
        status: form.status,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        customer_location: form.customer_location,
      };
      const res = selectedId
        ? await api.put(`/dashboard/subscriptions/${selectedId}`, payload)
        : await api.post('/dashboard/subscriptions', payload);
      toast.success(res.data?.message || 'Subscription saved');
      const item = res.data?.subscription || null;
      if (item?.id) {
        setSelectedId(item.id);
        setSelected(item);
      }
      setForm(item ? {
        title: item.title || '',
        amount: item.amount || '',
        currency: item.currency || 'UGX',
        billing_cycle: item.billing_cycle || 'monthly',
        start_date: item.start_date || '',
        next_due_date: item.next_due_date || '',
        end_date: item.end_date || '',
        status: item.status || 'active',
        customer_name: item.customer?.name || '',
        customer_phone: item.customer?.phone || '',
        customer_email: item.customer?.email || '',
        customer_location: item.customer?.location || '',
      } : DEFAULT_FORM);
      await load();
      if (item?.id) await loadDetail(item.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (id, path, body = {}, message = 'Action complete') => {
    try {
      const res = await api.post(`/dashboard/subscriptions/${id}${path}`, body);
      toast.success(res.data?.message || message);
      await load();
      await loadDetail(id);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const filtered = useMemo(() => {
    return (data.subscriptions || []).filter((item) => {
      const haystack = `${item.title || ''} ${item.customer?.name || ''} ${item.customer?.phone || ''} ${item.customer?.email || ''}`.toLowerCase();
      return !query || haystack.includes(query.toLowerCase());
    });
  }, [data.subscriptions, query]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Subscriptions</h1>
          <p className="text-slate-500 text-sm mt-0.5">Collect recurring payments and manage upcoming due dates.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={load}><Search size={14} className="mr-1" /> Refresh</Button>
          <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={resetForm}><Plus size={14} className="mr-1" /> New Subscription</Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Total</p><p className="mt-1 text-2xl font-semibold text-slate-900">{data.subscriptions?.length || 0}</p></CardContent></Card>
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Upcoming</p><p className="mt-1 text-2xl font-semibold text-slate-900">{data.upcoming?.length || 0}</p></CardContent></Card>
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Overdue</p><p className="mt-1 text-2xl font-semibold text-slate-900">{data.overdue?.length || 0}</p></CardContent></Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedId ? 'Edit Subscription' : 'Create Subscription'}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="grid gap-4 md:grid-cols-2">
            <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="Monthly membership" /></div>
            <div><Label>Amount</Label><Input className="mt-1" type="number" value={form.amount} onChange={(e) => update('amount', e.target.value)} placeholder="50000" /></div>
            <div><Label>Currency</Label><Input className="mt-1" value={form.currency} onChange={(e) => update('currency', e.target.value.toUpperCase())} /></div>
            <div>
              <Label>Billing Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => update('billing_cycle', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['daily', 'weekly', 'monthly', 'yearly'].map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Start Date</Label><Input className="mt-1" type="date" value={form.start_date} onChange={(e) => update('start_date', e.target.value)} /></div>
            <div><Label>Next Due Date</Label><Input className="mt-1" type="date" value={form.next_due_date} onChange={(e) => update('next_due_date', e.target.value)} /></div>
            <div><Label>End Date</Label><Input className="mt-1" type="date" value={form.end_date} onChange={(e) => update('end_date', e.target.value)} /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => update('status', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{['active', 'paused', 'cancelled', 'completed'].map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 grid gap-3 md:grid-cols-2">
              <div><Label>Customer Name</Label><Input className="mt-1" value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)} /></div>
              <div><Label>Customer Phone</Label><Input className="mt-1" value={form.customer_phone} onChange={(e) => update('customer_phone', e.target.value)} /></div>
              <div><Label>Customer Email</Label><Input className="mt-1" type="email" value={form.customer_email} onChange={(e) => update('customer_email', e.target.value)} /></div>
              <div><Label>Customer Location</Label><Input className="mt-1" value={form.customer_location} onChange={(e) => update('customer_location', e.target.value)} /></div>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>{saving ? 'Saving...' : 'Save Subscription'}</Button>
              {selectedId && <Button type="button" variant="outline" onClick={resetForm}>New</Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[1.35fr,1fr]">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Subscriptions</CardTitle>
            <Input className="mt-3" placeholder="Search subscriptions..." value={query} onChange={(e) => setQuery(e.target.value)} />
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="h-32 flex items-center justify-center"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Next Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id} className={selectedId === item.id ? 'bg-emerald-50/40' : ''} onClick={() => setSelectedId(item.id)} role="button">
                      <TableCell>
                        <div className="font-medium text-slate-900">{item.title}</div>
                        <div className="text-xs text-slate-500">{item.billing_cycle}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{item.customer?.name || '-'}</div>
                        <div className="text-xs text-slate-500">{item.customer?.phone || item.customer?.email || '-'}</div>
                      </TableCell>
                      <TableCell>{fmtMoney(item.currency, item.amount)}</TableCell>
                      <TableCell>
                        <div>{fmtDate(item.next_due_date)}</div>
                        {item.overdue ? <div className="text-xs text-red-600 flex items-center gap-1"><Clock3 size={12} /> overdue</div> : null}
                      </TableCell>
                      <TableCell><Badge className={statusClass(item.status, item.overdue)}>{item.overdue ? 'Overdue' : item.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); doAction(item.id, '/checkout', {}, 'Checkout ready'); }}>
                            Pay
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); doAction(item.id, '/share-whatsapp', {}, 'WhatsApp ready'); }}>
                            <MessageCircle size={14} className="mr-1" /> WhatsApp
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); doAction(item.id, '/send-email', {}, 'Email reminder sent'); }}>
                            <Mail size={14} className="mr-1" /> Email
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Due Payments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selected ? (
              <>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-xs text-slate-500">Next due</div>
                  <div className="font-medium text-slate-900">{fmtDate(selected.next_due_date)}</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-xs text-slate-500">Payment link</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(selected.payment_link_url || ''); toast.success('Payment link copied'); }}>
                      <Copy size={14} className="mr-1" /> Copy
                    </Button>
                    <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.location.href = selected.payment_link_url}>
                      <Repeat2 size={14} className="mr-1" /> Checkout
                    </Button>
                  </div>
                </div>
                <div className="rounded-xl bg-white border border-slate-100 p-3">
                  <div className="text-xs text-slate-500 mb-2">Payment History</div>
                  <div className="space-y-2">
                    {(selected.payments || []).length ? selected.payments.map((payment) => (
                      <div key={payment.id} className="border-b border-slate-100 pb-2 last:border-b-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-slate-900">{payment.title || 'Subscription payment'}</div>
                          <Badge className={statusClass(payment.status, payment.overdue)}>{payment.status}</Badge>
                        </div>
                        <div className="text-xs text-slate-500">{fmtDate(payment.due_date)} · {fmtMoney(payment.currency, payment.amount)}</div>
                      </div>
                    )) : <div className="text-sm text-slate-500">No payment history yet.</div>}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm text-slate-500">Select a subscription to view its payment link and history.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
